import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { api } from '../../utils/index'
import SetTitle from '../../utils/set_title'
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
  FormText,
  Alert,
} from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css';
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import { useFileUpload } from '../../hooks'

function VolumeRetrieveEditForm() {
  SetTitle('Market Screener')

  const { id } = useParams()
  const history = useHistory()

  // Record data (read-only except Monthly Update)
  const [volumeRetrieve, setVolumeRetrieve] = useState({
    name: '',
    schedule: false,
    locale: '',
    province: '',
    email: '',
    bq_table_name: '',
    brand: ''
  })

  const [file, setFile] = useState(null)
  const { upload, loading: saving } = useFileUpload()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const [keywordStats, setKeywordStats] = useState({ total: 0, filled: 0 })
  const [keywordStatsLoading, setKeywordStatsLoading] = useState(true)

  // Fetch record on report load and check if the report is retrieving data rn
  useEffect(() => {
    async function load() {
      try {
        // fetch the form data
        const resp = await api.get(`/market-screener-detail/${id}`)
        setVolumeRetrieve(resp.data)
        setLoading(false)

        // fetch the keywords filled over the total
        const keywordStatsResp = await api.get('keyword-volume/', {
          params: { volume_report_scheduled_id: id }
        })
        setKeywordStats({
          total: keywordStatsResp.data.total,
          filled: keywordStatsResp.data.filled
        })
      } catch (err) {
        setError('Error fetching record or keyword Statistic.')
      } finally {
        setLoading(false)
        setKeywordStatsLoading(false)
      }
    }
    load()
  }, [id])

  // can only edit once keywordStats are loaded and the report isn't running
  const canEdit = !keywordStatsLoading && (keywordStats.filled === keywordStats.total || keywordStats.filled === 0)

  // Handle form submission
const handleSubmit = async (e) => {
    e.preventDefault()

    const formData = new FormData()

    // Append CSV file if selected
    if (file) {
      formData.append('file', file)
    }

    // Append all other fields (read-only but sent for completeness)
    formData.append('name', volumeRetrieve.name)
    formData.append('schedule', volumeRetrieve.schedule)
    formData.append('locale', volumeRetrieve.locale)
    formData.append('province', volumeRetrieve.province)
    formData.append('email', volumeRetrieve.email)
    formData.append('bq_table_name', volumeRetrieve.bq_table_name)
    formData.append('brand', volumeRetrieve.brand)

    try {
      await upload('put', `/market-screener-detail/${id}`, formData, {
        onSuccess: () => {
          notify('Keywords Aggiornate Correttamente', 'success')
          setTimeout(() => {
            history.push('/market-screener')
          }, 3000) // 3-seconds delay
        },
      })
    } catch (err) {
      notify('Errore nel salvataggio', 'error')
      console.error('Save failed:', err)
    }
  }
  if (loading) {
    return <Spinner />
  }
  if (error) {
    return <div className="text-center text-danger">{error}</div>
  }

return (
  <>
    <div className="pb-5">
      <Card className="bg-secondary shadow">
        <CardHeader className="bg-white border-0">
          <Row style={{ marginLeft: "0px" }}>
            <Col md={10}>
              <h1 className="mb-0">Market Screener</h1>
            </Col>
            <Col md={2} style={{ textAlign: "right" }}>
              <Button color="warning" onClick={() => history.push('/market-screener')}>
                &lt;- Go Back
              </Button>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {/* Show warning if we’re still fetching keywordStats or not yet complete */}
          {keywordStatsLoading
            ? <Alert color="info">Checking keyword progress...</Alert>
            : !canEdit &&
              <Alert color="warning">
                This report is still running. You can only edit once all keywords are retrieved ({keywordStats.filled}/{keywordStats.total}).
              </Alert>
          }
          <Form onSubmit={handleSubmit} id="market-screener-form">
            {/* --- Row 1: File Input --- */}
            <Row>
              <Col md={6}>
                <FileInput
                  onFileChange={file => setFile(file)}
                  accept=".csv, .xlsx"
                  helpText={
                    <>
                      Carica un file contenente le tue keywords. <br />
                      <b>Il file deve contenere almeno una colonna "Keyword"</b> <br />
                      <b>
                        Le singole keyword non devono contenere questi simboli: @ \ ^ = ! ` {'<'} {'>'} {'['} {']'} {'('} {')'} % | ? ; ~
                      </b>
                    </>
                  }
                  required={false}
                />
              </Col>
            </Row>
            <br />

            {/* --- Row 2: Nome & Email --- */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="nome" style={{ display: "block" }}>
                    Nome
                  </Label>
                  <Input
                    className="form-control-alternative"
                    type="text"
                    name="nome"
                    id="nome"
                    value={volumeRetrieve.name}
                    readOnly
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="email" style={{ display: "block" }}>
                    Email
                  </Label>
                  <Input
                    className="form-control-alternative"
                    type="email"
                    name="email"
                    id="email"
                    value={volumeRetrieve.email}
                    readOnly
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* --- Row 3: Location & Province --- */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="location" style={{ display: "block" }}>
                    Location
                  </Label>
                  <Input
                    className="form-control-alternative"
                    type="text"
                    name="location"
                    id="location"
                    value={volumeRetrieve.locale}
                    readOnly
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="province" style={{ display: "block" }}>
                    Province
                  </Label>
                  <Input
                    className="form-control-alternative"
                    type="text"
                    name="province"
                    id="province"
                    value={volumeRetrieve.province}
                    readOnly
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* --- Row 4: Monthly Update Switch (editable) --- */}
            <Row className="mb-3">
              <Col md={6}>
                <FormGroup className="d-flex align-items-center gap-2">
                  <Input
                    type="switch"
                    id="schedule"
                    checked={volumeRetrieve.schedule}
                    onChange={(e) =>
                      setVolumeRetrieve({
                        ...volumeRetrieve,
                        schedule: e.target.checked
                      })
                    }
                  />
                  <Label htmlFor="schedule" className="mb-0">
                    Monthly Update
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            {/* --- Row 5: Submit Button --- */}
            <Row style={{ display: "flex", justifyContent: "flex-end", marginRight: "50px" }}>
              <Button color="primary" type="submit" disabled={saving || !canEdit}>
                {saving ? "Saving..." : "Save File & Update"}
              </Button>
            </Row>
          </Form>
          <ToastContainer />
        </CardBody>
      </Card>
    </div>
  </>
);


}

export default VolumeRetrieveEditForm
